import { useCallback, useEffect, useState } from "react";

import { api as publicApi } from "../api";
import { identifyYbug, nameFromEmail } from "../lib/ybug";
import { Autocheck } from "./Autocheck";
import { Docs } from "./Docs";
import { Marki } from "./Marki";
import { Records, RecordDetail, formatDate } from "./Records";
import { Schema } from "./Schema";
import { Users } from "./Users";
import { Webhooks } from "./Webhooks";
import { PageHead, Shell, type View } from "./Shell";
import {
  AdminError, admin, forgetKey, readKey, readSession, sessionEmail, storeKey, storeSession,
  type AdminStats, type MessageRow,
} from "./api";

/** Panel konsultanta pod /admin. Osobny świat od procesu kandydata. */
/**
 * Powrót z logowania Microsoftem.
 *
 * Wynik przychodzi we fragmencie adresu, bo ten — w odróżnieniu od query —
 * nie trafia do logów serwera ani do nagłówka `Referer`. Czytamy go raz, na
 * poziomie modułu (a nie w efekcie), i od razu czyścimy: token w pasku adresu
 * zostaje w historii przeglądarki.
 */
const powrotZLogowania = (() => {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const token = hash.get("token");
  const blad = hash.get("error");
  if (token) storeSession(token);
  if (token || blad) {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
  return { blad };
})();

/**
 * Karta sprawy ma własny adres: /admin/sprawa/{id}.
 *
 * Panel był jedną stroną bez adresów — otwarta teczka wyglądała w pasku
 * przeglądarki tak samo jak pulpit. Nie dało się jej wysłać, dodać do zakładek
 * ani wrócić do niej przyciskiem wstecz. A powiadomienie „ktoś prosi o pomoc"
 * jest warte tyle, ile prowadzący do niego link: bez adresu konsultant dostaje
 * wiadomość i szuka człowieka po nazwisku na liście czterdziestu spraw.
 */
function adresSprawy(): string | null {
  const dopasowanie = window.location.pathname.match(/\/admin\/sprawa\/([\w-]+)/);
  return dopasowanie ? dopasowanie[1] : null;
}

function zapamietajAdres(id: string | null): void {
  const adres = id ? `/admin/sprawa/${id}` : "/admin";
  if (window.location.pathname !== adres) {
    window.history.pushState({ sprawa: id }, "", adres);
  }
}

export default function AdminApp() {
  const [loginError, setLoginError] = useState<string | null>(powrotZLogowania.blad);
  const [authorised, setAuthorised] = useState(Boolean(readKey() || readSession()));
  const [view, setView] = useState<View>(() => (adresSprawy() ? "procesy" : "pulpit"));
  const [openRecord, setOpenRecord] = useState<string | null>(adresSprawy);
  const [schemaId, setSchemaId] = useState<string | null>(null);
  // Procesów bywa więcej niż jeden (legalizacja, kurier). Bez tej listy panel
  // brał pierwszy schemat z bazy i do drugiego nie dało się w ogóle dojść.
  const [sizes, setSizes] = useState<Record<string, number>>({});
  const [counts, setCounts] = useState<{ procesy?: number; schemat?: number }>({});
  // Rolę bierzemy z serwera, a nie z tokenu: token żyje kilkanaście godzin,
  // a odebranie praw ma być widoczne od razu.
  const [role, setRole] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const schemas = Object.keys(sizes);

  const otworzSprawe = (id: string | null) => {
    setOpenRecord(id);
    zapamietajAdres(id);
  };

  const refreshSchemas = useCallback(
    () =>
      admin
        .schemas()
        .then((rows) => Object.fromEntries(rows.map((row) => [row.schema_id, row.datapoints])))
        .then(setSizes),
    [],
  );

  // Wstecz i dalej w przeglądarce mają wracać tam, gdzie człowiek był.
  useEffect(() => {
    const naZmiane = () => {
      const id = adresSprawy();
      setOpenRecord(id);
      if (id) setView("procesy");
    };
    window.addEventListener("popstate", naZmiane);
    return () => window.removeEventListener("popstate", naZmiane);
  }, []);

  useEffect(() => {
    if (!authorised) return;
    admin
      .schemas()
      .then((rows) => {
        setSizes(Object.fromEntries(rows.map((row) => [row.schema_id, row.datapoints])));
        setSchemaId(rows[0]?.schema_id ?? null);
      })
      .catch(() => setAuthorised(false));
    admin
      .stats()
      .then((data) => setCounts((current) => ({ ...current, procesy: data.total })))
      .catch(() => undefined);
    admin
      .me()
      .then((kto) => {
        setRole(kto.role);
        // Zgłoszenie z panelu niesie, kto je wysłał: id, imię i nazwisko, e-mail, rola.
        const email = kto.actor.includes("@") ? kto.actor : null;
        publicApi.health()
          .then((health) => {
            const name = email ? nameFromEmail(email) : kto.actor;
            identifyYbug(health.ybug, {
              user: { id: kto.actor, name, email, role: kto.role },
              // Zgłoszenie z panelu ma od razu wpisane imię i e-mail konsultanta.
              feedback: { name, email },
              // Panel mówi po polsku niezależnie od języka kandydata.
              language: "pl",
            });
          })
          .catch(() => undefined);
      })
      .catch(() => setRole(null));
  }, [authorised]);

  if (!authorised) {
    return <Login error={loginError} onDismiss={() => setLoginError(null)} onDone={() => setAuthorised(true)} />;
  }

  return (
    <Shell
      view={view}
      // Licznik węzłów dotyczy wybranego procesu, nie pierwszego z bazy.
      counts={{ ...counts, schemat: schemaId ? sizes[schemaId] : undefined }}
      canManageUsers={role === "superadmin"}
      onSearch={setSearch}
      onView={(next) => {
        setOpenRecord(null);
        setView(next);
      }}
    >
      <main className="adm__main">
        {view === "pulpit" && (
          <Dashboard
            onStuck={() => setView("schemat")}
            onLogout={() => {
              forgetKey();
              setAuthorised(false);
            }}
          />
        )}
        {view === "procesy" &&
          (openRecord ? (
            <RecordDetail id={openRecord} onBack={() => otworzSprawe(null)} />
          ) : (
            <Records onOpen={otworzSprawe} search={search} schemas={schemas} />
          ))}
        {view === "schemat" &&
          (schemaId ? (
            <Schema
              schemaId={schemaId}
              schemas={schemas}
              // Świeżo założony proces musi od razu pojawić się w selektorze,
              // inaczej wygląda, jakby zniknął.
              onSchema={(id) => {
                setSchemaId(id);
                void refreshSchemas();
              }}
            />
          ) : (
            <div className="spinner" />
          ))}
        {view === "audyt" && (
          <section className="adm__section">
            <PageHead
              title="Audyt"
              subtitle="Co jest zepsute i co z tym zrobić — bez czekania, aż zauważy to człowiek"
            />
            <Autocheck />
          </section>
        )}
        {view === "marki" && <Marki />}
        {view === "dostepy" && <Users />}
        {view === "webhooki" && <Webhooks />}
        {view === "sms" && <Outbox />}
        {view === "api" && <Docs />}
      </main>
    </Shell>
  );
}

function Login({
  onDone,
  error: rejected,
  onDismiss,
}: {
  onDone: () => void;
  error: string | null;
  onDismiss: () => void;
}) {
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [microsoft, setMicrosoft] = useState(false);

  useEffect(() => {
    admin
      .authConfig()
      .then((config) => setMicrosoft(config.microsoft))
      .catch(() => setMicrosoft(false));
  }, []);

  const submit = async () => {
    setBusy(true);
    storeKey(key.trim());
    try {
      await admin.stats();
      onDone();
    } catch (caught) {
      forgetKey();
      setError((caught as AdminError).status === 401 ? "Klucz nie pasuje." : (caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gate" style={{ maxWidth: 380, margin: "14vh auto" }}>
      <h1 className="gate__title">Panel konsultanta</h1>

      {/* Odmowa z Microsoftu musi być widoczna i konkretna: „konto spoza listy"
          to inna akcja niż „logowanie wygasło". */}
      {rejected && (
        <div className="adm__alert adm__alert--error" onClick={onDismiss} role="alert">
          {rejected}
        </div>
      )}

      {/* Konto firmowe jest drogą dla ludzi: wygasa i zostawia w dzienniku
          nazwisko zamiast słowa „panel". Klucz zostaje niżej — dla integracji
          i na wypadek, gdyby logowanie było niedostępne. */}
      {microsoft && (
        <>
          <p className="gate__text">Zaloguj się kontem firmowym.</p>
          <button
            className="btn btn--primary"
            style={{ width: "100%" }}
            onClick={() => {
              window.location.href = "/api/admin/auth/microsoft/start";
            }}
          >
            Zaloguj przez Microsoft
          </button>
          <p className="gate__text" style={{ marginTop: 22 }}>
            Albo kluczem dostępu (integracje, awaria logowania):
          </p>
        </>
      )}
      {!microsoft && <p className="gate__text">Wpisz klucz dostępu. Lokalnie jest to „dev-admin".</p>}

      <div className="gate__row">
        <input
          className="input"
          type="password"
          autoFocus={!microsoft}
          placeholder="Klucz dostępu"
          value={key}
          onChange={(event) => setKey(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && submit()}
        />
        <button className="btn btn--primary" disabled={busy || !key.trim()} onClick={submit}>
          Wejdź
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function Dashboard({ onStuck, onLogout }: { onStuck: () => void; onLogout: () => void }) {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    admin
      .stats()
      .then(setStats)
      .catch((caught) => setError((caught as AdminError).message));
  }, []);

  useEffect(load, [load]);

  // Błąd nie zjada całej strony: nagłówek zostaje, komunikat staje tam,
  // gdzie miały być dane — widać, że to pulpit zawiódł, a nie cały panel.
  if (error) {
    return (
      <section className="adm__section">
        <PageHead title="Pulpit" subtitle={dzisiaj()} />
        <p className="error">{error}</p>
      </section>
    );
  }
  // Szkielet zamiast kółka: układ pulpitu stoi od razu, a po kafelkach
  // przesuwa się połysk (klatki `polysk` z tokens.css) — liczby wskoczą
  // w miejsca, w których wzrok już na nie czeka. Makieta powtarza układ
  // docelowy: ciemny widżet z boczną kolumną, rząd kart, rozkład etapów.
  if (!stats) {
    return (
      <section className="adm__section" aria-busy="true">
        <PageHead title="Pulpit" subtitle={dzisiaj()} />
        <div className="adm__today" aria-hidden="true">
          <div className="adm__panel adm__panel--dark">
            <span className="skel skel--title" />
            <span className="skel skel--line" />
            <span className="skel skel--line" />
            <span className="skel skel--line" />
          </div>
          <div className="adm__todayside">
            {[0, 1].map((slot) => (
              <div key={slot} className="adm__card">
                <span className="skel skel--label" />
                <span className="skel skel--value" />
              </div>
            ))}
          </div>
        </div>
        <div className="adm__cards" aria-hidden="true">
          {[0, 1, 2].map((slot) => (
            <div key={slot} className="adm__card">
              <span className="skel skel--label" />
              <span className="skel skel--value" />
            </div>
          ))}
        </div>
        <div className="adm__panel" aria-hidden="true">
          <span className="skel skel--title" />
          <span className="skel skel--line" />
          <span className="skel skel--line" />
        </div>
      </section>
    );
  }

  // Zatory rysują się względem najgorszego pytania — pasek mówi „to pytanie
  // trzyma połowę ludzi", zanim przeczyta się liczbę.
  const najwiekszyZator = Math.max(1, ...stats.stuck_on.map((row) => row.count));

  return (
    <section className="adm__section">
      <PageHead
        title="Pulpit"
        // Pulpit otwiera się co rano — data mówi od progu, że liczby są
        // z dzisiaj, a nie z ekranu zostawionego we wtorek.
        subtitle={dzisiaj()}
        actions={
          <>
            {/* Kto siedzi w panelu. Przy wspólnym kluczu to pytanie nie miało
                odpowiedzi — a od niej zależy, czyje nazwisko stoi w dzienniku. */}
            {sessionEmail() && <span className="adm__muted">{sessionEmail()}</span>}
            <button className="btn btn--ghost" onClick={onLogout}>
              Wyloguj
            </button>
          </>
        }
      />

      {/* Najpierw praca, potem statystyka. Pierwszy ekran odpowiada na „przy
          czym czekają ludzie": ciemny widżet z zatorami ściąga wzrok, obok
          stoją sprawy zablokowane (czekają na naszą decyzję) i liczba osób,
          które wypełniają w tej chwili. Suma procesów i procent dojść do
          końca to monitoring — schodzą rząd niżej. */}
      <div className="adm__today">
        <div className="adm__panel adm__panel--dark">
          <h2 className="adm__subtitle">Pytania, na których ludzie stoją</h2>
          <p className="adm__muted" style={{ marginTop: -6 }}>
            Powtarzający się węzeł to zwykle znak, że pytanie jest niejasne albo wymaga
            dokumentu, którego nikt nie ma pod ręką.
          </p>
          <ul className="adm__stuck">
            {stats.stuck_on.map((row) => (
              <li key={row.key}>
                <button className="adm__link" onClick={onStuck}>
                  {row.key}
                </button>
                {/* Pasek w pastelu, nie w czerwieni: zator to fakt do zbadania,
                    nie alarm — a czerwony znak na ciemnym tle jest zakazany. */}
                <span className="adm__stuckmeter" aria-hidden="true">
                  <i style={{ width: `${(row.count / najwiekszyZator) * 100}%` }} />
                </span>
                <span className="adm__mono">{row.count}</span>
              </li>
            ))}
            {stats.stuck_on.length === 0 && (
              <li className="adm__muted">Nikt nigdzie nie utknął — nic tu dzisiaj nie czeka.</li>
            )}
          </ul>
        </div>

        <div className="adm__todayside">
          <Card
            value={String(stats.blocked)}
            label="zablokowanych"
            tone={stats.blocked > 0 ? "warn" : undefined}
            note={stats.blocked > 0 ? "czekają na decyzję po naszej stronie" : "nic nie czeka na decyzję"}
          />
          <Card
            value={String(stats.open - stats.blocked)}
            label="w trakcie"
            note="wypełniają w tej chwili"
          />
        </div>
      </div>

      <div className="adm__cards">
        <Card value={String(stats.total)} label="procesów łącznie" delta={stats.delta.started} note="nowych w 7 dni" />
        <Card
          value={String(stats.completed)}
          label="zakończonych"
          tone="ok"
          delta={stats.delta.completed}
          note="w 7 dni"
        />
        <Card value={`${stats.completion_rate}%`} label="dochodzi do końca" note="ze wszystkich rozpoczętych" />
      </div>

      {/* Rozkład etapów na całej szerokości: etykiety nie łamią się w dwóch
          liniach, a paski mają dość miejsca, żeby różnice było widać. */}
      <div className="adm__panel">
        <h2 className="adm__subtitle">Gdzie są otwarte procesy</h2>
        {stats.by_stage.length === 0 && <p className="adm__muted">Brak otwartych procesów.</p>}
        {stats.by_stage.map((row) => (
          <div key={row.stage} className="adm__bar">
            <span className="adm__barlabel">{row.label}</span>
            <div className="meter">
              <span style={{ width: `${(row.count / Math.max(1, stats.open)) * 100}%` }} />
            </div>
            {/* Udział procentowy obok liczby: „17" nie mówi, czy to dużo —
                „81%" mówi. Oba w cyfrach tabelarycznych, równane do prawej. */}
            <span className="adm__barpct">{Math.round((row.count / Math.max(1, stats.open)) * 100)}%</span>
            <span className="adm__mono adm__barcount">{row.count}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Dzisiejsza data po ludzku — podtytuł pulpitu otwieranego co rano. */
function dzisiaj(): string {
  return `Stan procesów · ${new Date().toLocaleDateString("pl-PL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  })}`;
}

function Card({
  value,
  label,
  tone,
  delta,
  note,
}: {
  value: string;
  label: string;
  tone?: "ok" | "warn";
  delta?: { change: number; percent: number; direction: "up" | "down" | "flat" };
  note?: string;
}) {
  return (
    <div className={`adm__card${tone ? ` adm__card--${tone}` : ""}`}>
      <span className="adm__cardlabel">{label}</span>
      <span className="adm__cardvalue">{value}</span>
      {(delta || note) && (
        // Sama liczba nie mówi, czy jest lepiej — dopiero zmiana tydzień do
        // tygodnia albo podpis, co ta liczba znaczy dla dzisiejszej pracy.
        <span className="adm__cardfoot">
          {delta && (
            <span className={`trend trend--${delta.direction}`}>
              {delta.direction === "up" ? "↑" : delta.direction === "down" ? "↓" : "—"}
              {Math.abs(delta.change)}
            </span>
          )}
          {note}
        </span>
      )}
    </div>
  );
}

/** Skrzynka nadawcza trybu lokalnego — podgląd SMS-ów bez bramki. */
const KIND_LABEL: Record<string, string> = {
  magic_link: "zaproszenie",
  otp: "kod logowania",
  potwierdzenie: "potwierdzenie",
};

/**
 * Dziennik wysyłek — SMS-y i e-maile razem, bo konsultant pyta o jedno:
 * „czy to do niego dotarło".
 *
 * Treści wiadomości tu nie ma i nie powinno być: w SMS-ie jedzie kod
 * jednorazowy albo magic link, czyli sekret wystarczający do wejścia w cudzy
 * onboarding. Wystarczy rodzaj, adres i to, czy bramka przyjęła.
 */
function Outbox() {
  const [rows, setRows] = useState<MessageRow[]>([]);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    admin
      .messages({ onlyFailed })
      .then(setRows)
      .catch((caught) => setError((caught as AdminError).message));
  }, [onlyFailed]);

  if (error) return <p className="error">{error}</p>;

  return (
    <section className="adm__section">
      <PageHead
        title="Wiadomości"
        subtitle="Co wyszło do kandydatów i czy bramka to przyjęła"
        actions={
          <button
            className={`btn ${onlyFailed ? "btn--primary" : "btn--ghost"}`}
            onClick={() => setOnlyFailed((value) => !value)}
          >
            {onlyFailed ? "Pokaż wszystkie" : "Tylko nieudane"}
          </button>
        }
      />
      <div className="adm__tablewrap">
        <table className="adm__table">
          <thead>
            <tr>
              <th>Kanał</th>
              <th>Rodzaj</th>
              <th>Odbiorca</th>
              <th>Stan</th>
              <th>Kiedy</th>
            </tr>
          </thead>
          {/* Kaskada z tokens.css — wysyłki wchodzą kolejno, także po
              przełączeniu filtra „tylko nieudane". */}
          <tbody className="kaskada">
            {rows.map((row, index) => (
              <tr key={`${row.created_at}-${index}`} style={{ "--i": index } as React.CSSProperties}>
                <td>
                  <span className="adm__tag">{row.channel}</span>
                </td>
                <td>{KIND_LABEL[row.kind] ?? row.kind}</td>
                <td className="adm__mono">{row.to}</td>
                <td>
                  {row.delivered ? (
                    <span className="pill pill--completed">przyjęte</span>
                  ) : (
                    <span className="pill pill--open" title={row.error ?? undefined}>
                      {row.error ? row.error.slice(0, 60) : "odrzucone"}
                    </span>
                  )}
                </td>
                {/* Jeden format daty w całym panelu (formatDate) — sekundy
                    z pełnego toLocaleString były szumem, a kolumna ma się
                    czytać pionem. */}
                <td className="adm__muted adm__date">{formatDate(row.created_at)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="adm__empty">
                  {onlyFailed ? "Żadna wysyłka się nie odbiła." : "Nic jeszcze nie poszło."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
